import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Code, 
  Copy, 
  ExternalLink, 
  Key, 
  Zap, 
  Shield, 
  FileText, 
  Users,
  Webhook,
  CheckCircle,
  AlertCircle,
  Book,
  Terminal,
  Globe,
  ArrowLeft,
  Database,
  Lock,
  Layers,
  Activity
} from 'lucide-react';
import Card from '../components/atoms/Card';
import Button from '../components/atoms/Button';
import toast from 'react-hot-toast';

const APIDocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Code copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'authentication', label: 'Authentication', icon: Key },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'wills', label: 'Wills & Witness', icon: Users },
    { id: 'notarization', label: 'Notarization', icon: Shield },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'sdks', label: 'SDKs', icon: Code },
  ];

  const codeExamples = {
    javascript: {
      install: `npm install @aegisvault/sdk`,
      basic: `import { createAegisVaultClient } from '@aegisvault/sdk';

const client = createAegisVaultClient({
  apiKey: 'your_api_key',
  baseUrl: 'https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1'
});

// Upload and encrypt document
const result = await client.documents.create({
  file: fileBuffer,
  metadata: {
    name: 'contract.pdf',
    type: 'application/pdf',
    tags: ['legal', 'important']
  },
  encryption: { enabled: true },
  notarization: { enabled: true }
});

console.log('Document uploaded:', result.data.documentId);`,
      will: `// Create digital will
const will = await client.wills.create({
  title: 'My Digital Will',
  documentId: result.data.documentId,
  creator: {
    address: '0x123...',
    name: 'John Doe',
    email: 'john@example.com'
  },
  witnesses: [
    {
      address: '0x456...',
      name: 'Jane Smith',
      role: 'primary'
    }
  ],
  beneficiaries: [
    {
      address: '0x789...',
      name: 'Alice Doe',
      relationship: 'daughter',
      sharePercentage: 100
    }
  ]
});`
    },
    python: {
      install: `pip install aegisvault`,
      basic: `from aegisvault import AegisVaultClient

client = AegisVaultClient(
    api_key='your_api_key',
    base_url='https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1'
)

# Upload and encrypt document
with open('contract.pdf', 'rb') as f:
    result = client.documents.create(
        file=f.read(),
        metadata={
            'name': 'contract.pdf',
            'type': 'application/pdf',
            'tags': ['legal', 'important']
        },
        encryption={'enabled': True},
        notarization={'enabled': True}
    )

print(f"Document uploaded: {result['data']['document_id']}")`,
      will: `# Create digital will
will = client.wills.create(
    title='My Digital Will',
    document_id=result['data']['document_id'],
    creator={
        'address': '0x123...',
        'name': 'John Doe',
        'email': 'john@example.com'
    },
    witnesses=[
        {
            'address': '0x456...',
            'name': 'Jane Smith',
            'role': 'primary'
        }
    ],
    beneficiaries=[
        {
            'address': '0x789...',
            'name': 'Alice Doe',
            'relationship': 'daughter',
            'share_percentage': 100
        }
    ]
)`
    },
    curl: {
      basic: `curl -X POST https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/api-documents \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "base64_encoded_file_data",
    "metadata": {
      "name": "contract.pdf",
      "type": "application/pdf",
      "tags": ["legal", "important"]
    },
    "encryption": {"enabled": true},
    "notarization": {"enabled": true}
  }'`,
      will: `curl -X POST https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/api-wills \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "My Digital Will",
    "documentId": "doc_abc123",
    "creator": {
      "address": "0x123...",
      "name": "John Doe"
    },
    "witnesses": [
      {
        "address": "0x456...",
        "name": "Jane Smith",
        "role": "primary"
      }
    ]
  }'`
    }
  };

  const useCases = [
    {
      title: 'Law Firm Automation',
      description: 'Automatically encrypt and notarize legal documents, wills, and contracts',
      icon: Shield,
      features: ['Automated encryption', 'Blockchain notarization', 'Witness management', 'Compliance tracking']
    },
    {
      title: 'Healthcare Records',
      description: 'HIPAA-compliant patient record management with secure sharing',
      icon: FileText,
      features: ['HIPAA compliance', 'Patient consent', 'Doctor access control', 'Audit trails']
    },
    {
      title: 'Certificate Authority',
      description: 'Issue and verify certificates with blockchain proof',
      icon: CheckCircle,
      features: ['Certificate issuance', 'Public verification', 'Tamper-proof records', 'Batch processing']
    },
    {
      title: 'Corporate Documents',
      description: 'Secure corporate document management and sharing',
      icon: Users,
      features: ['Team collaboration', 'Access controls', 'Version tracking', 'Compliance reports']
    }
  ];

  const endpoints = [
    {
      method: 'POST',
      path: '/api-documents',
      description: 'Upload and encrypt documents',
      params: ['file', 'metadata', 'encryption', 'notarization']
    },
    {
      method: 'GET',
      path: '/api-documents',
      description: 'List user documents',
      params: ['page', 'limit', 'filter']
    },
    {
      method: 'POST',
      path: '/api-wills',
      description: 'Create digital will',
      params: ['title', 'documentId', 'creator', 'witnesses', 'beneficiaries']
    },
    {
      method: 'POST',
      path: '/notarize-document',
      description: 'Notarize document on blockchain',
      params: ['documentData', 'senderAddress', 'documentId']
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Aegis Vault API</h2>
              <p className="text-lg text-gray-600 mb-6">
                Programmatic access to secure document management, encryption, blockchain notarization, and digital will services.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold">Base URL</h3>
                  </div>
                  <code className="text-sm bg-gray-100 p-2 rounded block break-all">
                    https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1
                  </code>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Zap className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold">Rate Limits</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 1,000 requests/hour</li>
                    <li>• 100 notarizations/hour</li>
                    <li>• 10 will creations/minute</li>
                  </ul>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Activity className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold">API Status</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Uptime</span>
                      <span className="text-sm font-medium text-green-600">99.9%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Response Time</span>
                      <span className="text-sm font-medium text-green-600">{'<50ms'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Operational
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Database className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold">Available Endpoints</h3>
                  </div>
                  <div className="space-y-2">
                    {endpoints.map((endpoint, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded font-mono ${
                          endpoint.method === 'GET' ? 'bg-green-100 text-green-700' :
                          endpoint.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {endpoint.method}
                        </span>
                        <span className="text-sm font-mono text-gray-600">{endpoint.path}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Use Cases</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {useCases.map((useCase, index) => (
                  <motion.div
                    key={useCase.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="p-6 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <useCase.icon className="h-6 w-6 text-primary-600" />
                        </div>
                        <h4 className="text-lg font-semibold">{useCase.title}</h4>
                      </div>
                      <p className="text-gray-600 mb-4">{useCase.description}</p>
                      <ul className="space-y-2">
                        {useCase.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'authentication':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication</h2>
              <p className="text-gray-600 mb-6">
                All API requests require authentication using API keys. Include your API key in the Authorization header.
              </p>
            </div>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">API Key Authentication</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authorization Header
                  </label>
                  <div className="relative">
                    <code className="block bg-gray-100 p-3 rounded text-sm">
                      Authorization: Bearer YOUR_API_KEY
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Copy}
                      onClick={() => copyToClipboard('Authorization: Bearer YOUR_API_KEY')}
                      className="absolute top-2 right-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Example Request
                  </label>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
     -H "Content-Type: application/json" \\
     https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/api-documents`}
                    </pre>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Copy}
                      onClick={() => copyToClipboard(`curl -H "Authorization: Bearer YOUR_API_KEY" \\
     -H "Content-Type: application/json" \\
     https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/api-documents`)}
                      className="absolute top-2 right-2 text-gray-300 hover:text-white"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-yellow-200 bg-yellow-50">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Security Best Practices</h4>
                  <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                    <li>• Never expose API keys in client-side code</li>
                    <li>• Store API keys securely in environment variables</li>
                    <li>• Rotate API keys regularly</li>
                    <li>• Use different keys for development and production</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Getting Your API Key</h3>
              <div className="space-y-4">
                <p className="text-gray-600">
                  To get your API key, sign up for an Aegis Vault account and navigate to the API section in your dashboard.
                </p>
                <div className="flex gap-3">
                  <Button variant="primary" onClick={() => navigate('/auth')}>
                    Get API Key
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/dashboard')}>
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Documents API</h2>
              <p className="text-gray-600 mb-6">
                Upload, encrypt, and manage documents with automatic IPFS storage and blockchain notarization.
              </p>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Upload Document</h3>
                <div className="space-y-4">
                  <div>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">POST /api-documents</code>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1">
                      <div className="flex gap-2 mb-4">
                        {Object.keys(codeExamples).map((lang) => (
                          <Button
                            key={lang}
                            variant={selectedLanguage === lang ? 'primary' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedLanguage(lang)}
                            className="capitalize"
                          >
                            {lang}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="lg:col-span-2">
                      <div className="relative">
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                          {codeExamples[selectedLanguage as keyof typeof codeExamples].basic}
                        </pre>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Copy}
                          onClick={() => copyToClipboard(codeExamples[selectedLanguage as keyof typeof codeExamples].basic)}
                          className="absolute top-2 right-2 text-gray-300 hover:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Response Format</h3>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`{
  "success": true,
  "data": {
    "documentId": "doc_abc123",
    "ipfsCid": "QmXYZ...",
    "encryptionKey": "encrypted_key_for_api_user",
    "uploadedAt": "2024-01-15T10:30:00Z",
    "status": "encrypted"
  }
}`}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Copy}
                    onClick={() => copyToClipboard(`{
  "success": true,
  "data": {
    "documentId": "doc_abc123",
    "ipfsCid": "QmXYZ...",
    "encryptionKey": "encrypted_key_for_api_user",
    "uploadedAt": "2024-01-15T10:30:00Z",
    "status": "encrypted"
  }
}`)}
                    className="absolute top-2 right-2 text-gray-300 hover:text-white"
                  />
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Request Parameters</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-medium">Parameter</th>
                        <th className="text-left py-2 font-medium">Type</th>
                        <th className="text-left py-2 font-medium">Required</th>
                        <th className="text-left py-2 font-medium">Description</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-mono">file</td>
                        <td className="py-2">string</td>
                        <td className="py-2">Yes</td>
                        <td className="py-2">Base64 encoded file data</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-mono">metadata.name</td>
                        <td className="py-2">string</td>
                        <td className="py-2">Yes</td>
                        <td className="py-2">File name</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-mono">metadata.type</td>
                        <td className="py-2">string</td>
                        <td className="py-2">No</td>
                        <td className="py-2">MIME type</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-mono">encryption.enabled</td>
                        <td className="py-2">boolean</td>
                        <td className="py-2">No</td>
                        <td className="py-2">Enable encryption</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-mono">notarization.enabled</td>
                        <td className="py-2">boolean</td>
                        <td className="py-2">No</td>
                        <td className="py-2">Enable blockchain notarization</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        );

      case 'wills':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Wills & Witness API</h2>
              <p className="text-gray-600 mb-6">
                Create and manage digital wills with cryptographic witness verification and automated execution.
              </p>
            </div>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Create Digital Will</h3>
              <div className="space-y-4">
                <div>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">POST /api-wills</code>
                </div>
                
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {codeExamples[selectedLanguage as keyof typeof codeExamples].will}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Copy}
                    onClick={() => copyToClipboard(codeExamples[selectedLanguage as keyof typeof codeExamples].will)}
                    className="absolute top-2 right-2 text-gray-300 hover:text-white"
                  />
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <h4 className="font-semibold mb-3">Witness Management</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Add/remove witnesses</li>
                  <li>• Cryptographic verification</li>
                  <li>• Automated wellness checks</li>
                  <li>• Multi-signature support</li>
                </ul>
              </Card>

              <Card className="p-6">
                <h4 className="font-semibold mb-3">Beneficiary Control</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Define beneficiaries</li>
                  <li>• Set share percentages</li>
                  <li>• Access level controls</li>
                  <li>• Conditional inheritance</li>
                </ul>
              </Card>

              <Card className="p-6">
                <h4 className="font-semibold mb-3">Execution Triggers</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Time-based triggers</li>
                  <li>• Witness confirmations</li>
                  <li>• External events</li>
                  <li>• Manual execution</li>
                </ul>
              </Card>
            </div>
          </div>
        );

      case 'notarization':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Notarization API</h2>
              <p className="text-gray-600 mb-6">
                Notarize documents on the Algorand blockchain for immutable proof of authenticity.
              </p>
            </div>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Notarize Document</h3>
              <div className="space-y-4">
                <div>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">POST /notarize-document</code>
                </div>
                
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`curl -X POST https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/notarize-document \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "documentData": "base64_encoded_document",
    "senderAddress": "ALGORAND_ADDRESS",
    "documentId": "doc_abc123",
    "fileName": "contract.pdf"
  }'`}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Copy}
                    onClick={() => copyToClipboard(`curl -X POST https://hxhwwrmhfgfiqynouxuf.supabase.co/functions/v1/notarize-document \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "documentData": "base64_encoded_document",
    "senderAddress": "ALGORAND_ADDRESS",
    "documentId": "doc_abc123",
    "fileName": "contract.pdf"
  }'`)}
                    className="absolute top-2 right-2 text-gray-300 hover:text-white"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Response Format</h3>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`{
  "success": true,
  "transactionId": "NOTARIZE_1234567890_abc123",
  "documentHash": "sha256_hash_of_document",
  "timestamp": "2024-01-15T10:30:00Z",
  "status": "confirmed"
}`}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                  onClick={() => copyToClipboard(`{
  "success": true,
  "transactionId": "NOTARIZE_1234567890_abc123",
  "documentHash": "sha256_hash_of_document",
  "timestamp": "2024-01-15T10:30:00Z",
  "status": "confirmed"
}`)}
                  className="absolute top-2 right-2 text-gray-300 hover:text-white"
                />
              </div>
            </Card>

            <Card className="p-6 border-blue-200 bg-blue-50">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Blockchain Integration</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Notarization uses the Algorand blockchain for immutable proof. Each notarization creates a permanent record that can be verified independently.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'webhooks':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Webhooks</h2>
              <p className="text-gray-600 mb-6">
                Receive real-time notifications for document events, notarizations, and will executions.
              </p>
            </div>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Webhook Events</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">document.uploaded</p>
                    <p className="text-sm text-gray-600">Triggered when a document is successfully uploaded</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">document.notarized</p>
                    <p className="text-sm text-gray-600">Triggered when a document is notarized on blockchain</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium">will.created</p>
                    <p className="text-sm text-gray-600">Triggered when a digital will is created</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium">will.executed</p>
                    <p className="text-sm text-gray-600">Triggered when a will is executed</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Webhook Payload Example</h3>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`{
  "event": "document.notarized",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "documentId": "doc_abc123",
    "transactionId": "ALGO_TX_123",
    "status": "confirmed",
    "documentHash": "sha256_hash"
  },
  "signature": "hmac_signature"
}`}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                  onClick={() => copyToClipboard(`{
  "event": "document.notarized",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "documentId": "doc_abc123",
    "transactionId": "ALGO_TX_123",
    "status": "confirmed",
    "documentHash": "sha256_hash"
  },
  "signature": "hmac_signature"
}`)}
                  className="absolute top-2 right-2 text-gray-300 hover:text-white"
                />
              </div>
            </Card>
          </div>
        );

      case 'sdks':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SDKs & Libraries</h2>
              <p className="text-gray-600 mb-6">
                Official SDKs and libraries to integrate Aegis Vault into your applications.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Terminal className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold">JavaScript/Node.js</h3>
                </div>
                <div className="space-y-3">
                  <code className="block bg-gray-100 p-2 rounded text-sm">
                    npm install @aegisvault/sdk
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ExternalLink}
                    className="w-full"
                  >
                    View Documentation
                  </Button>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Terminal className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Python</h3>
                </div>
                <div className="space-y-3">
                  <code className="block bg-gray-100 p-2 rounded text-sm">
                    pip install aegisvault
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ExternalLink}
                    className="w-full"
                  >
                    View Documentation
                  </Button>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Terminal className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Go</h3>
                </div>
                <div className="space-y-3">
                  <code className="block bg-gray-100 p-2 rounded text-sm">
                    go get github.com/aegisvault/go-sdk
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ExternalLink}
                    className="w-full"
                  >
                    View Documentation
                  </Button>
                </div>
              </Card>
            </div>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Start Example</h3>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`// Install the SDK
${codeExamples[selectedLanguage as keyof typeof codeExamples].install}

// Basic usage
${codeExamples[selectedLanguage as keyof typeof codeExamples].basic}`}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                  onClick={() => copyToClipboard(`${codeExamples[selectedLanguage as keyof typeof codeExamples].install}\n\n${codeExamples[selectedLanguage as keyof typeof codeExamples].basic}`)}
                  className="absolute top-2 right-2 text-gray-300 hover:text-white"
                />
              </div>
            </Card>
          </div>
        );

      default:
        return <div>Content for {activeTab}</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                icon={ArrowLeft}
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">API Documentation</h1>
                  <p className="text-gray-600">Integrate Aegis Vault into your applications</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/auth')}>
                Get API Key
              </Button>
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Navigation Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-8">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-200
                      ${activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 font-medium border border-primary-200'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <tab.icon size={18} className="flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocumentationPage;